import { useState } from "react";

import Button from "components/Button";
import HollowButton from "components/HollowButton";

import localLawService from "api/localLawService";
import { ILegalActListItem } from "dto/LocalLaw/ILocalLaw";
import { ENotificationType, notification } from "utils/notification";

import styles from "./styles.module.scss";

interface Props {
  act: ILegalActListItem;
  onClose: () => void;
  onAdopted: () => void;
}

// Uchwalenie / wydanie aktu — nadanie numeru i daty. W Magistracie numer nadawało
// biuro rady dopiero po sesji, dlatego projekt do tej chwili może go nie mieć.

const errorText = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { Message?: string } } })?.response?.data?.Message ??
  fallback;

const AdoptModal = ({ act, onClose, onAdopted }: Props) => {
  const [actNumber, setActNumber] = useState(act.ActNumber ?? "");
  const [adoptedDate, setAdoptedDate] = useState(
    (act.AdoptedDate ?? new Date().toISOString()).slice(0, 10),
  );
  const [comment, setComment] = useState("");

  const { mutate: adopt, isLoading } = localLawService.useAdopt();

  const canApply = !!actNumber.trim() && !!adoptedDate;

  const submit = () => {
    if (!canApply) return;

    adopt(
      {
        id: act.Id,
        request: {
          ActNumber: actNumber.trim(),
          AdoptedDate: adoptedDate,
          Comment: comment.trim() || null,
        },
      },
      {
        onSuccess: onAdopted,
        onError: (e) =>
          notification({
            type: ENotificationType.ERROR,
            title: "Błąd",
            text: errorText(e, "Nie udało się uchwalić aktu."),
          }),
      },
    );
  };

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div className={styles.modalTitle}>Uchwalenie aktu</div>

        <div className={styles.modalBody}>
          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Numer aktu</label>
              <input
                className={styles.textInput}
                value={actNumber}
                autoFocus
                placeholder="np. XIV/122/2026"
                onChange={(e) => setActNumber(e.target.value)}
              />
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel}>Data uchwalenia</label>
              <input
                className={styles.textInput}
                type="date"
                value={adoptedDate}
                onChange={(e) => setAdoptedDate(e.target.value)}
              />
            </div>
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>Uwagi</label>
            <textarea
              className={styles.textArea}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </div>

          <span className={styles.hint}>
            Numer trafia do metryki i do nagłówka dokumentu na Dysku. Po uchwaleniu akt
            można przekazać do publikacji.
          </span>
        </div>

        <div className={styles.modalFooter}>
          <HollowButton text="Anuluj" onClick={onClose} />
          <Button
            text="Uchwal"
            loading={isLoading}
            disabled={!canApply}
            onClick={submit}
          />
        </div>
      </div>
    </div>
  );
};

export default AdoptModal;
